import clsx from 'clsx';
import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

import { IconCircleCheck, IconCircleInfo, IconCircleX, IconCrossSmall, IconWarning } from '../icons';

export type SnackbarVariant = 'error' | 'info' | 'success' | 'warning';

export type SnackbarProps = HTMLAttributes<HTMLDivElement> & {
    readonly closeLabel?: string;
    readonly message: ReactNode;
    readonly onClose?: () => void;
    readonly variant?: SnackbarVariant;
};

const baseClasses =
    'pointer-events-auto inline-flex w-full min-w-[280px] max-w-[420px] items-center gap-m rounded-md border px-m py-s font-sans text-sm font-normal shadow-md';

const variantClasses: Record<SnackbarVariant, string> = {
    error: 'bg-error-25 border-error-200 text-error-700',
    info: 'bg-info-25 border-info-100 text-info-300',
    success: 'bg-success-25 border-success-100 text-success-600',
    warning: 'bg-warning-50 border-warning-200 text-warning-800',
};

const variantIcons: Record<SnackbarVariant, ReactNode> = {
    error: <IconCircleX size={20} />,
    info: <IconCircleInfo size={20} />,
    success: <IconCircleCheck size={20} />,
    warning: <IconWarning size={20} />,
};

export const Snackbar = forwardRef<HTMLDivElement, SnackbarProps>(
    ({ className, closeLabel = 'Close', message, onClose, variant = 'info', ...props }, ref) => (
        <div ref={ref} className={clsx(baseClasses, variantClasses[variant], className)} role="alert" {...props}>
            <span className="flex shrink-0 items-center">{variantIcons[variant]}</span>
            <span className="flex-1">{message}</span>
            {onClose !== undefined && (
                <button
                    aria-label={closeLabel}
                    className="m-0 flex shrink-0 cursor-pointer appearance-none items-center rounded-sm border-0 bg-transparent p-0 font-sans leading-none text-inherit opacity-70 outline-none transition-opacity duration-200 ease hover:opacity-100 focus-visible:outline focus-visible:outline-current"
                    onClick={onClose}
                    type="button"
                >
                    <IconCrossSmall size={16} />
                </button>
            )}
        </div>
    ),
);

Snackbar.displayName = 'Snackbar';
